import type { SxProps } from "@mui/material";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AnyObject } from "@entrilia/shared/types";
import type { MeasureDescriptor } from "../../../../../../../../api/types/svTypes.ts";
import { toAmountType, toCalculateBy } from "../../../../utilities/measureConverter.ts";
import FormulaInput from "./FormulaInput";
import FormulaInputWrapper from "./FormulaInputWrapper";
import FunctionTooltip from "./FunctionTooltip";
import Intellisense from "./Intellisense";
import { useFormulaInputState } from "./hooks/formulaInputState";
import type { CustomMeasureReturnType } from "./hooks/useCustomMeasure";
import { styleFormulaNodes } from "./utilities/formulaNodeStyling";
import { toAmountCalculateByCaption } from "./utilities/measureConverter";
import type { Token } from "./utilities/tokenizer";
import { TokenType } from "./utilities/tokenizer";

const DEFAULT_SHOW_VALIDATION_ERROR_DELAY = 3000;

interface Props {
  measure?: MeasureDescriptor;
  state: CustomMeasureReturnType;
  editable: boolean;
  onValidated?: (valid: boolean) => void;
  showCalculatedBy?: boolean;
  hideCheck?: boolean;
  sx?: SxProps;
  formulaStructureError?: string;
}

const FormulaContainer = ({
  measure,
  state,
  editable,
  onValidated,
  showCalculatedBy,
  hideCheck,
  sx,
  formulaStructureError,
}: Props) => {
  const inputState = useFormulaInputState();
  const [formulaError, setFormulaError] = useState<string>();
  const [formulaFocused, setFormulaFocused] = useState(false);
  const formulaErrorRef = useRef<string | undefined>(undefined);

  const [displayingError, setDisplayingError] = useState<string>();
  const [inputTrigger, setInputTrigger] = useState<AnyObject>();

  const hoveredFunctionElement = state.functionHovered?.element ?? null;

  useEffect(() => {
    formulaErrorRef.current = formulaError;
  }, [formulaError]);

  useEffect(() => {
    styleFormulaNodes(state.formulaNodes);
  }, [state.formulaNodes]);

  useEffect(() => {
    onValidated?.call(null, formulaError === undefined);
  }, [formulaError, onValidated]);

  const onFailed = useCallback(
    (tokens: Token[]) => {
      const invalidToken = tokens.find((token) => !!token.error);
      let error: string | undefined = undefined;
      if (invalidToken !== undefined) {
        error = `${invalidToken.error}${invalidToken.tokenType !== TokenType.Eof ? `: ${invalidToken.value}` : ""}`;
      }
      setFormulaError(error);
    },
    [setFormulaError]
  );

  useEffect(() => {
    const delayMs = formulaFocused ? DEFAULT_SHOW_VALIDATION_ERROR_DELAY : 0;
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    if (!formulaErrorRef.current && !formulaStructureError) {
      setDisplayingError(undefined);
      return;
    }
    timeoutId = setTimeout(() => {
      setDisplayingError(formulaErrorRef.current || formulaStructureError);
    }, delayMs);

    return () => clearTimeout(timeoutId);
  }, [setDisplayingError, inputTrigger, formulaStructureError, formulaFocused]);

  const calculateBy = useMemo(() => {
    if (measure === undefined) return "";
    const calcBy = toCalculateBy(measure.units);
    if (calcBy === undefined) return "";
    const amountType = toAmountType(measure.units);
    return toAmountCalculateByCaption(calcBy, amountType);
  }, [measure]);

  return (
    <Fragment key={"FormulaContainer"}>
      <Stack spacing={0.5} useFlexGap sx={{ width: "100%", ...sx }}>
        <Tooltip
          title={<FunctionTooltip state={state} />}
          placement="top"
          arrow
          disableInteractive
          open={hoveredFunctionElement !== null}
          slotProps={{
            popper: {
              hidden: hoveredFunctionElement === null,
              anchorEl: {
                getBoundingClientRect: () => {
                  return new DOMRect(
                    (hoveredFunctionElement?.getBoundingClientRect().x || 0) + 15,
                    hoveredFunctionElement?.getBoundingClientRect().y,
                    0,
                    0
                  );
                },
              },
            },
          }}
        >
          <Box />
        </Tooltip>
        <Box sx={{ display: "flex", paddingBottom: 1, justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography id="custom_measure_formula_title" variant="subtitle1">
              Formula
            </Typography>
          </Box>
          {showCalculatedBy && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography>Calculate by</Typography>
              <Typography color="primary">{calculateBy}</Typography>
            </Box>
          )}
        </Box>
        <FormulaInputWrapper
          state={state}
          focused={formulaFocused}
          formulaError={!!displayingError}
          hideCheck={hideCheck}
        >
          <FormulaInput
            key={measure?.name || "formula_input_key"}
            onSuccess={() => {
              setFormulaError(undefined);
              setDisplayingError(undefined);
            }}
            onFailed={onFailed}
            state={state}
            editable={editable}
            formulaState={inputState}
            actions={inputState.actions}
            defaultFormula={measure?.formula}
            onFocus={() => setFormulaFocused(true)}
            onBlur={() => {
              setFormulaFocused(false);
              setFormulaError(formulaErrorRef.current);
              setDisplayingError(formulaErrorRef.current);
            }}
            onInput={() => setInputTrigger({})}
          />
        </FormulaInputWrapper>
        {displayingError && (
          <Typography variant="caption" sx={(theme) => ({ color: theme.palette.error.dark })}>
            {displayingError}
          </Typography>
        )}
        {!displayingError && editable && (
          <Typography
            variant="caption"
            sx={(theme) => ({ height: editable ? "20px" : "inherit", color: theme.palette.secondary.main })}
          >
            Example DPI: A/(B*-1)
          </Typography>
        )}
        <Intellisense state={state} formulaState={inputState} actions={inputState.actions} />
      </Stack>
    </Fragment>
  );
};

export default FormulaContainer;
